import {
  isPullRequest,
  isPullResponse,
  SYNC_PROTOCOL_LIMITS
} from '@/domain/models/sync/contract';
import { applyAuthoritativeChanges } from './applyAuthoritativeChanges';

export const SYNC_FROM_SERVER_STATUS = Object.freeze({
  SUCCESS: 'success',
  ERROR: 'error',
  RESYNC_REQUIRED: 'resync_required'
});

const invalidResponseError = () =>
  new TypeError('Invalid synchronization pull response');

/**
 * Creates a service that synchronizes one authenticated user's local database
 * from the server.
 *
 * The service owns orchestration only. HTTP remains behind `pullApi`, while
 * domain state changes remain behind the LocalDatabase apply service.
 *
 * @param {Object} options - Synchronization dependencies and configuration.
 * @param {Object} options.database - LocalDatabase adapter.
 * @param {{pull: Function}} options.pullApi - Pull transport adapter.
 * @param {number} [options.pageSize] - Number of changes requested per page.
 * @param {Function} [options.applyPage] - Atomic page application function.
 * @returns {{syncFromServer: Function}} Server-to-local synchronization service.
 */
export const createWebLocalDatabaseSyncFromServer = ({
  database,
  pullApi,
  pageSize = SYNC_PROTOCOL_LIMITS.max_page_size,
  applyPage = applyAuthoritativeChanges
}) => {
  /**
   * Synchronizes the selected owner's local database until the server returns
   * an empty page. A failed page leaves the last committed cursor untouched.
   *
   * @param {{ownerId: string, manageConnection?: boolean}} options -
   *   Authenticated owner context and connection ownership.
   * @returns {Promise<Object>} Typed synchronization outcome.
   */
  const syncFromServer = ({ ownerId, manageConnection = true }) => {
    let lastSeenServerVersion = 0;
    let pages = 0;
    const changedEntities = {};

    return Promise.resolve()
      .then(() => (manageConnection ? database.close() : undefined))
      .then(() => (manageConnection ? database.open({ ownerId }) : undefined))
      .then(() => database.getSyncCursor())
      .then((cursor) => {
        lastSeenServerVersion = cursor.last_seen_server_version;
        return undefined;
      })
      .then(function pullNextPage() {
        const request = {
          last_seen_server_version: lastSeenServerVersion,
          page_size: pageSize
        };

        if (!isPullRequest(request)) {
          return Promise.reject(
            new TypeError('Invalid synchronization request')
          );
        }

        return pullApi.pull(request).then((response) => {
          if (!isPullResponse(response)) {
            throw invalidResponseError();
          }

          pages += 1;

          if (response.resync_required) {
            return {
              status: SYNC_FROM_SERVER_STATUS.RESYNC_REQUIRED,
              pages,
              last_seen_server_version: lastSeenServerVersion,
              changed_entities: changedEntities
            };
          }

          if (response.authoritative_changes.length === 0) {
            return {
              status: SYNC_FROM_SERVER_STATUS.SUCCESS,
              pages,
              last_seen_server_version: lastSeenServerVersion,
              changed_entities: changedEntities
            };
          }

          return applyPage({
            database,
            changes: response.authoritative_changes
          }).then((pageChangesByType) => {
            Object.entries(pageChangesByType).forEach(
              ([entityType, pageChanges]) => {
                if (!changedEntities[entityType]) {
                  changedEntities[entityType] = [];
                }
                changedEntities[entityType].push(...pageChanges);
              }
            );
            return database.getSyncCursor().then((cursor) => {
              lastSeenServerVersion = cursor.last_seen_server_version;
              return pullNextPage();
            });
          });
        });
      })
      .catch((error) => ({
        status: SYNC_FROM_SERVER_STATUS.ERROR,
        pages,
        last_seen_server_version: lastSeenServerVersion,
        changed_entities: changedEntities,
        error
      }));
  };

  return { syncFromServer };
};
